import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "@lib/firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNotification } from "../../common/NotificationContext";
import { InputField } from "@cakrawalajakarta/shared-components";
import { 
    LuArrowLeft, 
    LuSave,
    LuLoader
} from "react-icons/lu";

export const VoucherCreate = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [formData, setFormData] = useState({
    code: "",
    discountType: "percentage",
    discountValue: "",
    allowedCategory: "",
    maxUsage: "",
    eventId: "JOTR2026",
    expiredAt: ""
  });

  // Fetch categories from JOTR2026 event
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const eventDoc = await getDoc(doc(db, "semuaKegiatan", "JOTR2026"));
        if (eventDoc.exists()) {
          const data = eventDoc.data();
          if (data.categories && Array.isArray(data.categories)) {
            setCategories(data.categories.map(c => c.categoryName));
          }
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code) {
      showNotification("error", "Voucher code is required");
      return;
    }
    if (!formData.discountValue) {
      showNotification("error", "Discount value is required");
      return;
    }

    try {
      setLoading(true);

      const voucherData = {
        code: formData.code.toUpperCase(),
        discountType: formData.discountType,
        discountValue: Number(formData.discountValue),
        discountPercentage: formData.discountType === "percentage" ? Number(formData.discountValue) : 0,
        allowedCategory: formData.allowedCategory || null,
        isActive: true,
        eventId: formData.eventId || null,
        maxUsage: formData.maxUsage ? Number(formData.maxUsage) : null,
        usageCount: 0,
        createdAt: new Date(),
        expiredAt: formData.expiredAt || null
      };

      await setDoc(doc(db, "vouchers", voucherData.code), voucherData);
      showNotification("success", "Voucher created successfully");
      navigate("/admin/vouchers");
    } catch (error) {
      console.error("Error saving voucher:", error);
      showNotification("error", "Failed to save voucher");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 pb-10">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin/vouchers")}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors text-slate-600"
          >
            <LuArrowLeft size={18} />
          </button>
          <h1 className="text-2xl font-bold text-slate-900">Create New Voucher</h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="space-y-4">
          {/* Row 1: Voucher Code | Allowed Categories Dropdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Voucher Code"
              name="code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="E.G. EARLYBIRD2025"
              labelColor="text-slate-700"
            />
            <InputField
              type="select"
              label="Allowed Category (Optional)"
              name="allowedCategory"
              value={formData.allowedCategory}
              onChange={(e) => setFormData({ ...formData, allowedCategory: e.target.value })}
              options={[
                { value: "", label: "All Categories" },
                ...categories.map(cat => ({ value: cat, label: cat }))
              ]}
              labelColor="text-slate-700"
            />
          </div>

          {/* Row 2: Discount Type | Discount Value */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              type="select"
              label="Discount Type"
              name="discountType"
              value={formData.discountType}
              onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
              options={[
                { value: "percentage", label: "Percentage (%)" },
                { value: "fixed", label: "Fixed Amount (IDR)" }
              ]}
              labelColor="text-slate-700"
            />
            <InputField
              type="number"
              label="Discount Value"
              name="discountValue"
              min="0"
              value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
              placeholder={formData.discountType === "percentage" ? "20" : "50000"}
              labelColor="text-slate-700"
            />
          </div>

          {/* Row 3: Max Usage | Expiry Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              type="number"
              label="Max Usage"
              name="maxUsage"
              min="1"
              value={formData.maxUsage}
              onChange={(e) => setFormData({ ...formData, maxUsage: e.target.value })}
              placeholder="100"
              labelColor="text-slate-700"
            />
            <InputField
              type="date"
              label="Expiry Date"
              name="expiredAt"
              value={formData.expiredAt}
              onChange={(e) => setFormData({ ...formData, expiredAt: e.target.value })}
              labelColor="text-slate-700"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => navigate("/admin/vouchers")}
              className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-900 font-medium transition-colors flex items-center gap-2"
            >
              {loading ? <LuLoader className="animate-spin" size={16} /> : <LuSave size={16} />}
              {loading ? "Saving..." : "Create Voucher"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
